import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ElementRef, ViewChild, inject, signal } from "@angular/core";
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { debounceTime } from "rxjs";
import { AutoComplete } from "../models/material-common-models";
import { DataService } from "../../features/bucket-items/service/data.service";
import { MatFormField, MatLabel, MatInput, MatError } from "@angular/material/input";
import { NgStyle, NgClass } from "@angular/common";
import { MatAutocompleteTrigger, MatAutocomplete, MatOption } from "@angular/material/autocomplete";
import { TooltipDisableDirective } from "../custom-directives/tooltip.directive";

@Component({
    selector: "app-autocomplete",
    templateUrl: "./autocomplete.component.html",
    styleUrls: ["./autocomplete.component.scss"],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, NgStyle, NgClass, MatLabel, MatInput, MatAutocompleteTrigger, MatAutocomplete, MatOption, MatError, TooltipDisableDirective]
})
export class AutoCompleteComponent implements OnInit, OnChanges {
  private dataService = inject(DataService);

  @ViewChild('inputField') inputTextField: ElementRef<HTMLInputElement>;

  @Input() public placeHolder: string = "Enter the Name";
  @Input() public floatLabelOnBlur: string = "Name";
  @Input() public floatLabelOnFocus: string = "Name";
  @Input() public autocompleteClassName: string = "";
  @Input() public formFieldClassName: string = "";
  @Input() public warningFieldClassName: string = "";
  @Input() public errorFieldClassName: string = "";
  @Input() public requiredErrorMessage: string = `${this.floatLabelOnFocus} is required`;
  @Input() public minType: number = 0;
  @Input() public options:AutoComplete[];
  @Input() public textFormControl: FormControl = null;
  @Input() public inputPattern: string = null;
  @Input() public inputValidationErrorMessage: string = `Please provide valid ${this.floatLabelOnFocus}`;
  @Input() public noResultsWarningMessage: string = "User not found, please enter PUID and proceed";
  @Input() public minTypeWarningMessage: string = "";
  @Input() public width: number = 16;
  @Input() public widthForOptions: number = 20.5;
  @Input() public internalFilter: boolean = false;

  @Output() public isOnFocus = new EventEmitter<boolean>();
  @Output() public valueChange = new EventEmitter<string>();
  @Output() public isValueSelected = new EventEmitter<boolean>();

  public autocompleteForm = new FormGroup({ 
    textFormControl: new FormControl(null, [
      Validators.required
    ])
  });
  public filteredOptions = signal<AutoComplete[]>([]);
  public filteredOptionsLength: number = 0;
  public inputOnFocus: boolean = false;
  public valueSelected:boolean = false;

  public ngOnInit(): void {
    this.enableScroll();
    if(this.minTypeWarningMessage.length===0){
       this.minTypeWarningMessage = `Enter at least ${this.minType} characters to search`;
    }
    if(this.textFormControl){
      this.autocompleteForm.controls.textFormControl = this.textFormControl;
    }
    this.filteredOptions.set(this._internalFilter(""));

    this.autocompleteForm.get('textFormControl').valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if(this.internalFilter){
        this.filteredOptions.set(this._internalFilter(this.autocompleteForm.controls.textFormControl.value));
        this.filteredOptionsLength = this._internalFilter(this.autocompleteForm.controls.textFormControl.value)?.length;
      }
      else{
        this._externalFilter(this.autocompleteForm.controls.textFormControl.value);      
      }
      this.customPatternValidator();
      this.enableScroll();
    });
  }

  public ngOnChanges(changes:SimpleChanges): void {
      if(changes?.options?.currentValue && this.autocompleteForm?.controls?.textFormControl?.value?.length >= this.minType){
        this.filteredOptions.set(changes?.options?.currentValue);
        this.filteredOptionsLength = changes?.options?.currentValue?.length;
        this.enableScroll();
      }
    }

  public optionsSelected(){
    this.valueSelected = true;
    this.isValueSelected.emit(this.valueSelected);
    this.inputTextField.nativeElement.blur();
  }

  public setIsonFocus(){
    if(this.inputOnFocus){
      this.valueSelected = false;
      this.isValueSelected.emit(this.valueSelected);
    }
    if(this.inputPattern?.length>0){
      this.customPatternValidator();
    }
    this.enableScroll();
    this.isOnFocus.emit(this.inputOnFocus);
  }
  
  private _externalFilter(value:string){
    if(value?.length >= this.minType){
      this.valueChange.emit(value);
    }
    else{
      this.filteredOptions.set([]);
      this.filteredOptionsLength = 0;
    }
  }

  private _internalFilter(value:string):AutoComplete[]{
    const filterValue = value?.toLowerCase();
    if(filterValue?.length >= this.minType){
      return this.options.filter(Option=>Option?.SuggestionText?.toLowerCase().includes(filterValue));
    }
    else{
      return [];
    }
  }

  customPatternValidator() {
    const pattern = new RegExp(this.inputPattern,"g");
    const value = this.textFormControl?.value;

    if (!value || pattern?.test(value) || this.valueSelected || this.inputOnFocus || !this.inputPattern || this.options.find(x=>x.DisplayTextForSelectedValue.toLowerCase()===value.toLowerCase())?.SelectedValue?.length>=3) {
      delete this.textFormControl?.errors?.pattern;
      this.textFormControl?.setErrors(this.textFormControl?.errors?.length !== 0 ? this.textFormControl?.errors : null);
    } else {
      this.textFormControl?.setErrors({ pattern: true });
    }
  }

  public enableScroll(){
    if((this.inputOnFocus && this.filteredOptionsLength>0)){
      this.dataService.setAutoCompleteActiveStatus(true);
    }
    else{
      this.dataService.setAutoCompleteActiveStatus(false);
    }
  }

  public buildAutocompleteId(controlId: string): string {
    return `autocomplete-${controlId}`;
  }
}
